export const calculateWarehouseLocationsOccupancyDateTimeCategoryRelations = () => {
    const now = new Date();

    // Always work in UTC
    const utcYear = now.getUTCFullYear();
    const utcMonth = now.getUTCMonth();
    const utcDate = now.getUTCDate();
    const utcHour = now.getUTCHours();

    let from;
    let to;

    if (utcHour < 20) {
        // Before 8 PM UTC → yesterday 8 PM → today 8 PM
        from = new Date(Date.UTC(utcYear, utcMonth, utcDate - 1, 20, 0, 0));
        to = new Date(Date.UTC(utcYear, utcMonth, utcDate, 20, 0, 0));
    } else {
        // After 8 PM UTC → today 8 PM → tomorrow 8 PM
        from = new Date(Date.UTC(utcYear, utcMonth, utcDate, 20, 0, 0));
        to = new Date(Date.UTC(utcYear, utcMonth, utcDate + 1, 20, 0, 0));
    }

    return { from, to };
}

// Pivot view settings (heatmap, trend pills, display mode) with safe defaults so reports saved
// before these settings existed still render. Stored on currentReport.pivot.
export const pivotViewModes = {
  NORMAL: "normal",
  PERIOD_OVER_PERIOD: "periodOverPeriod",
  COMPARE_PREVIOUS: "comparePrevious",
  COMPARE: "compare",
};

// Keys usable in currentReport.pivotHidden to hide parts of the pivot settings popover.
// SETTINGS (or hiding every section) hides the gear button entirely.
export const pivotSettings = {
  HEATMAP: "heatmap",
  TRENDS: "trends",
  MODE: "mode",
  SETTINGS: "settings",
};

// The configurable sections in the gear popover, in display order — also used to detect "everything
// hidden". MODE is excluded: it has its own icon toggle in the pivot header, not the popover.
export const pivotSettingSections = [
  pivotSettings.HEATMAP,
  pivotSettings.TRENDS,
];

// Trend indicator variants — each is one column (header label + row pill) in the pivot. Order here is the
// column order. `trends` on currentReport.pivot holds the list of enabled variants.
export const pivotTrendVariants = {
  LAST_YEAR: "lastYear",
  PREVIOUS: "previous",
  CURRENT: "current",
};

export const pivotTrendVariantList = [
  pivotTrendVariants.LAST_YEAR,
  pivotTrendVariants.PREVIOUS,
  pivotTrendVariants.CURRENT,
];

// `trends` used to be a boolean (all on / all off); it's now the list of enabled variants. Normalize both
// forms: true -> every variant, false/undefined -> none, array -> kept in canonical order.
const normalizeTrends = (trends) => {
  if (trends === true) return [...pivotTrendVariantList];
  if (Array.isArray(trends)) return pivotTrendVariantList.filter((v) => trends.includes(v));
  return [];
};

// A pivot row can only be drilled into when it carries an entity id — without one
// there is nothing to fetch a detail for, so the row stays inert.
export const isPivotRowDrillable = (row) => row?.id !== undefined && row?.id !== null;

export const pivotRowCursor = (row) => {
  if (!isPivotRowDrillable(row)) return "default";
  return "pointer";
};

export const getPivotView = (currentReport) => ({
  heatmap: currentReport?.pivot?.heatmap ?? "off",
  trends: normalizeTrends(currentReport?.pivot?.trends),
  mode: currentReport?.pivot?.mode ?? pivotViewModes.NORMAL,
});

// Chart categories arrive from the backend either as raw strings ("manual", a date) or as option
// objects ({id, description}). Resolve the display text, then run it through i18next: enum-like
// values ("manual") pick up their translation, anything without a key (webshop names, carrier
// names, dates) falls through unchanged because i18next returns unknown keys as-is.
export const translateChartCategory = (item, t) => {
  const raw = typeof item === "string"
    ? item
    : item?.description || item?.name || item?.label || String(item?.id || "");
  if (!raw) return raw;
  return t(raw);
};

export const purgeNonSerializable = (obj) => {
  if (!obj || typeof obj !== "object") return obj;

  // If it's an array, map recursively
  if (Array.isArray(obj)) {
    return obj.map(v => purgeNonSerializable(v));
  }

  return Object.fromEntries(
    Object.entries(obj)
      .filter(([_, value]) => {
        // keep primitives and plain objects/arrays
        if (
          value === null ||
          typeof value === "string" ||
          typeof value === "number" ||
          typeof value === "boolean"
        ) {
          return true;
        }
        if (Array.isArray(value) || (typeof value === "object" && value.constructor === Object)) {
          return true;
        }
        // everything else (function, class instance, symbol) is removed
        return false;
      })
      .map(([key, value]) => [key, purgeNonSerializable(value)])
  );
};
